import React from "react";
import { useMetaTitleSetter } from "src/hooks/meta";
import { useAgentMarketCampaignListRequest } from "src/hooks/api/agent/market-campaign";
import CenterPreloader from "src/components/loaders/CenterPreloader";
import MarketCampaignCard from "src/pages/agent/market-campaigns/components/MarketCampaignCard";
import Row from "react-bootstrap/esm/Row";
import Col from "react-bootstrap/esm/Col";
import TablePagination from "src/components/table/TablePagination";

const MyMarketCampaignsPage: React.FC = () => {
  const title = "Market campaigns Agent Dashboard";
  useMetaTitleSetter(title);

  // Local pagination state
  const [limit, setLimit] = React.useState<10 | 25 | 50 | 100 | 200>(25);
  const [offset, setOffset] = React.useState(0);

  // Create pagination params object
  const paginationParams = React.useMemo(
    () => ({
      limit,
      offset,
    }),
    [limit, offset],
  );

  const { data, isFetching, refetch } = useAgentMarketCampaignListRequest(paginationParams);

  // Calculate pagination values
  const totalCount = data?.data.count || 0;
  const totalPages = React.useMemo(() => Math.max(1, Math.ceil(totalCount / limit)), [totalCount, limit]);
  const currentPage = Math.floor(offset / limit) + 1;

  // Pagination handlers
  const goToPage = (page: number) => {
    const safePage = Math.min(Math.max(1, page), totalPages);
    const newOffset = (safePage - 1) * limit;
    setOffset(newOffset);
  };

  const handleLimitChange = (newLimit: 10 | 25 | 50 | 100 | 200) => {
    setLimit(newLimit);
    setOffset(0); // Reset to first page when changing page size
  };

  // Handle refresh when market campaign is updated
  const handleMarketCampaignUpdate = React.useCallback(() => {
    refetch();
  }, [refetch]);

  if (isFetching) {
    return <CenterPreloader />;
  }

  return (
    <section>
      <Row className="d-flex align-items-stretch">
        {data?.data.results.map((marketCampaign) => (
          <Col key={marketCampaign.id} className="mb-3 d-flex" xs={24} sm={12} md={8} lg={6}>
            <MarketCampaignCard key={marketCampaign.id} marketCampaign={marketCampaign} onUpdated={handleMarketCampaignUpdate} />
          </Col>
        ))}
      </Row>

      {totalCount > 0 && (
        <div className="d-flex justify-content-center mt-4">
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onFirst={() => goToPage(1)}
            onPrev={() => goToPage(currentPage - 1)}
            onNext={() => goToPage(currentPage + 1)}
            onLast={() => goToPage(totalPages)}
            limit={limit}
            onLimitChange={handleLimitChange}
            disabled={isFetching}
            selectDisabled={isFetching}
            pageText={`Page ${currentPage} of ${totalPages}`}
            selectSide="top"
          />
        </div>
      )}
    </section>
  );
};

export default MyMarketCampaignsPage;
